import { SiweMessage, generateNonce } from 'siwe';
import { toUnicode, toASCII } from 'punycode';
import { ethers } from 'ethers';

class InvalidStatementException extends Error {
    static invalidStatement() {
        const message = 'InvalidStatementException: Invalid statement';
        return new InvalidStatementException(message);
    }
    constructor(message){
        super(message);
    }
}

class InvalidENSException extends Error {
    static invalidENSFormat() {
        const message = 'InvalidENSException: Invalid ENS format';
        return new InvalidENSException(message);
    }
    static notRegisteredENS(ENS) {
        const message = `InvalidENSException: ENS ${ENS} is not registered`;
        return new InvalidENSException(message);
    }
    static invalidENSOwner(ENS, address) {
        const message = `InvalidENSException: ENS ${ENS} is not owned by address ${address}`;
        return new InvalidENSException(message);
    }
    static chainNotSupported(chainId) {
        const message = `InvalidENSException: Chain ${chainId} is not supported`;
        return new InvalidENSException(message);
    }
    constructor(message){
        super(message);
    }
}

class InvalidTimeException extends Error {
    static timeExceedsMaxSafeInteger() {
        const message = 'InvalidTimeException: Time exceeds max safe integer';
        return new InvalidTimeException(message);
    }
    static timeValueLessThanZero() {
        const message = 'InvalidTimeException: Time value less than zero';
        return new InvalidTimeException(message);
    }
    static requiredTTL() {
        const message = 'InvalidTimeException: TTL is required';
        return new InvalidTimeException(message);
    }
    constructor(message){
        super(message);
    }
}

class InvalidConfigurationException extends Error {
    static providerUrlRequired() {
        const message = 'InvalidConfigurationException: Provider URL is required';
        return new InvalidConfigurationException(message);
    }
    static domainRequired() {
        const message = 'InvalidConfigurationException: Domain is required';
        return new InvalidConfigurationException(message);
    }
    constructor(message){
        super(message);
        this.name = 'InvalidConfigurationException';
    }
}

function _extends() {
    _extends = Object.assign || function assign(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source)if (Object.prototype.hasOwnProperty.call(source, key)) target[key] = source[key];
        }
        return target;
    };
    return _extends.apply(this, arguments);
}

function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}

function checkTTL(ttl) {
    if (ttl <= 0) {
        throw InvalidTimeException.timeValueLessThanZero();
    }
    if (ttl + Date.now() >= Number.MAX_SAFE_INTEGER) {
        throw InvalidTimeException.timeExceedsMaxSafeInteger();
    }
    return true;
}

function checkDomainValid(domain) {
    const parts = domain.split(".");
    if (parts.length < 2 || parts.length > 3) {
        throw InvalidENSException.invalidENSFormat();
    }
    return true;
}

function constructSignInStatement(subname, statement = "") {
    return `I am signing in with my ENS: ${subname}` + (statement ? `, ${statement}` : "");
}
function extractDataFromStatement(statement) {
    const regex = /I am signing in with my ENS: ([a-zA-Z0-9-_.]+)/;
    const result = regex.exec(statement);
    if (!result) {
        throw InvalidStatementException.invalidStatement();
    }
    return {
        ens: result[1]
    };
}

// inspired by spruceid siwe: https://github.com/spruceid/siwe/blob/main/packages/siwe/lib/ethersCompat.ts
const ethersCompat = ethers;
const getJsonRpcProvider = (providerUrl, chainId)=>{
    if ('JsonRpcProvider' in ethersCompat) {
        return new ethersCompat.JsonRpcProvider(providerUrl, chainId);
    } else {
        return new ethersCompat.providers.JsonRpcProvider(providerUrl, chainId);
    }
};
'namehash' in ethersCompat ? ethersCompat.namehash : ethersCompat.utils.namehash;
'getAddress' in ethersCompat ? ethersCompat.getAddress : ethersCompat.utils.getAddress;

class SIWENS extends SiweMessage {
    async verify(params, opts) {
        let verification;
        try {
            const { signature } = params, rest = _object_without_properties_loose(params, [
                "signature"
            ]);
            const _tempParams = _extends({
                signature
            }, rest);
            const lastByte = parseInt(signature.slice(-2), 16);
            if (lastByte < 27) {
                const adjustedV = (27 + lastByte % 2).toString(16).padStart(2, '0');
                _tempParams['signature'] = signature.slice(0, -2) + adjustedV;
            }
            verification = await super.verify(_tempParams, opts);
        } catch (e) {
            const statement = e.data.statement;
            const { ens } = extractDataFromStatement(statement);
            throw _extends({}, e, {
                ens: toUnicode(ens)
            });
        }
        const statement = verification.data.statement;
        if (!statement) {
            throw InvalidStatementException.invalidStatement();
        }
        const { ens: asciiEns } = extractDataFromStatement(statement);
        const ens = toUnicode(asciiEns);
        await this.verifyEnsAddress(ens, this.address);
        return _extends({}, verification, {
            ens
        });
    }
    static generateIssuedAndExpirationTime(ttl) {
        const date = new Date();
        const issuedAt = date.toISOString();
        const expirationTime = new Date(date.getTime() + ttl).toISOString();
        return {
            issuedAt,
            expirationTime
        };
    }
    static generateNonce() {
        return generateNonce();
    }
    async verifyEnsAddress(ens, address) {
        const resolvedAddress = await this.provider.resolveName(ens);
        if (!resolvedAddress) {
            throw InvalidENSException.notRegisteredENS(ens);
        }
        if (resolvedAddress !== address) {
            throw InvalidENSException.invalidENSOwner(ens, address);
        }
        return true;
    }
    constructor(signInConfig){
        const { params, providerUrl } = signInConfig;
        if (typeof params === 'string') {
            super(params);
            if (!providerUrl) {
                throw InvalidConfigurationException.providerUrlRequired();
            }
            this.provider = getJsonRpcProvider(providerUrl);
            this.providerUrl = providerUrl;
            return;
        }
        if (!params.ttl) {
            throw InvalidTimeException.requiredTTL();
        }
        if (!params.domain) {
            throw InvalidConfigurationException.domainRequired();
        }
        let issuedAt = params.issuedAt;
        let expirationTime = params.expirationTime;
        if (params.ttl) {
            checkTTL(params.ttl);
            const { issuedAt: issuedAtGenerated, expirationTime: expirationTimeGenerated } = SIWENS.generateIssuedAndExpirationTime(params.ttl);
            issuedAt = issuedAt || issuedAtGenerated;
            expirationTime = expirationTime || expirationTimeGenerated;
        }
        checkDomainValid(params.ens);
        const statement = constructSignInStatement(toASCII(params.ens), (params == null ? void 0 : params.statement) || '');
        super(_extends({}, params, {
            statement,
            version: params.version || '1',
            issuedAt,
            expirationTime
        }));
        this.providerUrl = providerUrl;
        this.provider = getJsonRpcProvider(providerUrl);
    }
}

export { InvalidConfigurationException, InvalidENSException, InvalidStatementException, InvalidTimeException, SIWENS, checkDomainValid, checkTTL, constructSignInStatement, extractDataFromStatement };
